import React, { createContext, useContext, useReducer, Dispatch } from 'react';
import type { AppState, AppAction } from '@/contexts/AppContext';

interface AppContextType {
  state: AppState;
  dispatch: Dispatch<AppAction>;
}

export const AppContext = createContext<AppContextType | null>(null);


export interface AssessmentResponse {
  id: string;
  userId: string;
  pulseResponses?: (number | string)[];
  pulseOpenResponse?: string;
  shapeResponses?: number[];
  completedAt?: Date;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AppState {
  assessment: AssessmentResponse | null;
  chatMessages: ChatMessage[];
}

type AppAction = 
  | { type: 'SET_ASSESSMENT'; payload: AssessmentResponse }
  | { type: 'UPDATE_ASSESSMENT'; payload: Partial<AssessmentResponse> }
  | { type: 'ADD_CHAT_MESSAGE'; payload: ChatMessage }
  | { type: 'SET_CHAT_MESSAGES'; payload: ChatMessage[] }
  | { type: 'LOGOUT' };

const initialState: AppState = {
  assessment: null,
  chatMessages: [],
};

const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_ASSESSMENT':
      return { ...state, assessment: action.payload };
    case 'UPDATE_ASSESSMENT':
      return { 
        ...state, 
        assessment: state.assessment ? { ...state.assessment, ...action.payload } : null 
      };
    case 'ADD_CHAT_MESSAGE':
      return { ...state, chatMessages: [...state.chatMessages, action.payload] };
    case 'SET_CHAT_MESSAGES':
      return { ...state, chatMessages: action.payload };
    case 'LOGOUT':
      return initialState;
    default:
      return state;
  }
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
